import { DayObject } from "@/types/date"
import { Airline } from "@/types/flights/airline"
import { FlightTraveler } from "@/types/flights/flight-booking"
import { FlightBookingApi } from "@/types/flights/flight-booking-api"
import { CurrentUser } from "@/types/user"
import { Nullable, Optional } from "@/types/util"
import uniqBy from "lodash/uniqBy"
import { z } from "zod"

import {
  BirthDaySchema,
  BookingFormOptions,
  CreditCardState,
  emptyCreditCardFormValue,
  FlightSeatSchema,
  FrequentFlyerSchema,
  GenderSchema,
  KnownTravelerNumberSchema,
  LapInfantSchema,
  MealPreferenceSchema,
  RedressNumberSchema,
  SeatPreferenceSchema,
  UserNameSchema,
  validateCreditCardFormValue,
} from "./form-utils"
import {
  BillingState,
  emptyBillingFormValue,
  validateBillingFormFormValue,
} from "./phone-utils"

export interface FlightBookingFormOptions extends BookingFormOptions {
  meal_preferences: { code: string; name: string }[]
}

export interface FlightTravelerFormValue {
  first_name: string
  last_name: string
  middle_name: string
  date_of_birth: Optional<DayObject>
  gender: FlightTraveler["gender"] | undefined
  lap_infant: "yes" | "no" | undefined
  seat_preference: Exclude<FlightTraveler["seat_preference"], null>
  meal_preference: Exclude<FlightTraveler["meal_preference"], null>
  redress_number: string
  known_traveler_number: string
  frequent_flyer_numbers: {
    airline_iata: string
    airline_name: string
    value: string
  }[]
  flight_seats: { flight_number: number; number: string; label: string }[]
}

export function emptyFlightTravelerFormValue(
  airlines: Airline[]
): FlightTravelerFormValue {
  return {
    first_name: "",
    last_name: "",
    middle_name: "",
    date_of_birth: undefined,
    gender: undefined,
    lap_infant: undefined,
    seat_preference: undefined,
    meal_preference: undefined,
    redress_number: "",
    known_traveler_number: "",
    frequent_flyer_numbers: airlines.map((airline) => ({
      airline_iata: airline.iata,
      airline_name: airline.name,
      value: "",
    })),
    flight_seats: [],
  }
}

const flightTravelerFormSchema = z.object({
  ...UserNameSchema.shape,
  ...BirthDaySchema.shape,
  ...GenderSchema.shape,
  ...SeatPreferenceSchema.shape,
  ...RedressNumberSchema.shape,
  ...KnownTravelerNumberSchema.shape,
  ...LapInfantSchema.shape,
  ...MealPreferenceSchema.shape,
  ...FrequentFlyerSchema.shape,
  ...FlightSeatSchema.shape,
})

export type FlightTravelerFormErrors = z.core.$ZodFormattedError<
  z.output<typeof flightTravelerFormSchema>
>

export type FlightTravelerFormValidated = z.infer<
  typeof flightTravelerFormSchema
>

export function validateTravelerFormValue(
  value: FlightTravelerFormValue,
  formOptions: FlightBookingFormOptions
):
  | [true, FlightTravelerFormValidated, null]
  | [false, null, FlightTravelerFormErrors] {
  const schemaWithOptions = flightTravelerFormSchema.refine(
    (val) => {
      if (
        val.meal_preference &&
        !formOptions.meal_preferences.find(
          (option) => option.code === val.meal_preference
        )
      ) {
        return false
      }

      return true
    },
    {
      path: ["meal_preference"],
      message: "Invalid meal preference",
    }
  )
  const res = schemaWithOptions.safeParse(value)

  if (res.success) {
    return [true, res.data, null]
  }

  return [false, null, z.formatError(res.error)]
}

export type TravelersState = [
  FlightTravelerFormValue,
  Nullable<FlightTravelerFormValidated>,
  Nullable<FlightTravelerFormErrors>,
][]

export type ValidatedTravelersState = [
  FlightTravelerFormValue,
  FlightTravelerFormValidated,
  null,
][]

export type FlightBookingFormState = {
  valid: boolean
  showValidations: boolean
  submitting: boolean
  submitError: string | undefined
  travelers: TravelersState
  credit_card: CreditCardState
  billing: BillingState
}

export function calcBookingFormValid(value: FlightBookingFormState): boolean {
  const validatedTravelers = value.travelers.map((t) => t[1])
  const validatedCreditCard = value.credit_card[1]
  const validatedBilling = value.billing[1]
  return (
    validatedTravelers.every((t) => t != null) &&
    validatedCreditCard != null &&
    validatedBilling != null
  )
}

export function emptyBookingFormState(
  flightBooking?: Optional<FlightBookingApi>,
  formOptions?: Optional<FlightBookingFormOptions>,
  user?: CurrentUser
): FlightBookingFormState {
  const uniqAirlines =
    uniqBy(
      flightBooking?.flight_itinerary?.flight_slices
        .flatMap((x) => x.flight_legs)
        .flatMap((x) => x.properties.airlines),
      "iata"
    ) ?? []

  const total = flightBooking
    ? flightBooking.flight_itinerary.properties.adults +
      flightBooking.flight_itinerary.properties.children
    : 0

  const travelers: TravelersState = Array(total)
    .fill(0)
    .map((_, ix) => {
      let values = emptyFlightTravelerFormValue(uniqAirlines)
      if (ix === 0 && user) {
        values = {
          ...values,
          first_name: user.first_name ?? "",
          last_name: user.last_name ?? "",
        }
      }

      if (!formOptions) {
        return [values, null, null]
      }
      const [, validated, errors] = validateTravelerFormValue(
        values,
        formOptions
      )
      return [values, validated, errors]
    })

  const ccValues = emptyCreditCardFormValue()
  let credit_card: CreditCardState
  if (!formOptions) {
    credit_card = [ccValues, null, null]
  } else {
    const [_, ccValidated, ccErrors] = validateCreditCardFormValue(
      emptyCreditCardFormValue(),
      formOptions
    )
    credit_card = [ccValues, ccValidated, ccErrors]
  }

  const billingValues = emptyBillingFormValue()

  let billing: BillingState
  if (!formOptions) {
    billing = [billingValues, null, null]
  } else {
    const [__, billingValidated, billingErrors] = validateBillingFormFormValue(
      emptyBillingFormValue(),
      formOptions,
      "flight"
    )

    billing = [billingValues, billingValidated, billingErrors]
  }

  return {
    valid: false,
    showValidations: false,
    submitting: false,
    submitError: undefined,
    travelers: travelers,
    credit_card: credit_card,
    billing: billing,
  }
}
